import { OrderProduct } from 'components'
import { OrderProductForm } from 'components/forms'
import { InnerPage } from 'components/layout'
import { TitleIconList } from 'components/lists'
import Spinner from 'components/Spinner/Spinner'
import VendorOrderShipments from 'components/VendorOrderShipment'
import { pick } from 'lodash'
import { get } from 'lodash'
import { bool, func, shape, string } from 'prop-types'
import React, { useEffect, useReducer } from 'react'
import { FiChevronRight, FiLifeBuoy } from 'react-icons/fi'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Col, Row } from 'reactstrap'
import { getUserType, isAdmin } from 'utils/auth'
import {
  CARB_CERTIFICATE_COUNTRIES,
  MATERIALS,
  ORDER_IMAGE_TYPES,
  USER_TYPES
} from 'utils/constants'
import { dtFormat } from 'utils/dateTime'
import { notifyMappedError } from 'utils/errors'

import { Part, PickupForm } from './components'
import {
  addImages,
  init,
  removeFilesCertification,
  removeImages,
  updatePickupAddressAndTime,
  updateShipmentData,
  uploadFilesCertification
} from './data/mw'
import { initialState, reducer } from './data/reducer'
import st from './VendorOrderDetail.module.css'

const VendorOrderDetail = ({
  dispatch: dispatchRedux,
  isAdmin,
  userType,
  intl,
  match: {
    params: { orderNumber, vendor }
  }
}) => {
  const shipmentAllowedUserTypes = [
    USER_TYPES.VENDOR,
    USER_TYPES.LOGISTICS,
    USER_TYPES.ADMIN
  ]
  const menuItems = [
    {
      icon: <FiLifeBuoy />,
      path: `mailto:catalogo@artemest.com`,
      text: intl.formatMessage({ id: 'Need Help? Contact Us' })
    },
    {
      icon: <FiChevronRight />,
      path: `/vendors/${vendor}/orders`,
      text: intl.formatMessage({ id: 'Back To List' })
    }
  ]
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    init({
      dispatch,
      dispatchRedux,
      orderNumber,
      vendor
    })
  }, [orderNumber, vendor])

  const handleSubmit =
    ({ shipmentNumber }) =>
    (data, initialFormValues) => {
      updateShipmentData(
        { ...data, shipmentNumber, orderNumber },
        state.lastSubmittedValues[data.productId] || initialFormValues,
        dispatch
      )
    }

  const handleImageAdd = (type, values) => (e) => {
    return new Promise((onDone) => {
      addImages({ imageFiles: e.target.files, type, values }, dispatch, onDone)
    })
  }

  const handleImageRemove =
    (type, values, orderManagementId, productId, lastSubmittedValues) =>
    ({ path, id }) => {
      return new Promise((onDone) => {
        removeImages(
          {
            type,
            values,
            path,
            orderManagementId,
            id,
            productId,
            lastSubmittedValues
          },
          dispatch,
          onDone
        )
      })
    }

  const handleImageError = () => {
    notifyMappedError({
      message: 'Error uploading image'
    })
  }

  const handlePickupFormSumbit = (data) => {
    updatePickupAddressAndTime(
      { orderNumber, vendorId: vendor, formData: data },
      dispatch
    )
  }

  const handleFileUploadCertification = (data) => {
    return new Promise((onDone) => {
      uploadFilesCertification(data, dispatch, onDone)
    })
  }

  const handleFileDeleteCertification = (data) => {
    return new Promise((onDone) => {
      removeFilesCertification(data, dispatch, onDone)
    })
  }

  const {
    ordersAreLoading,
    products,
    status,
    ordersLoading,
    imagesLoading,
    lastSubmittedValues,
    pickupFormDataFromContact,
    pickupFormLoading,
    imagesPending,
    certificationFiles,
    certificationFilesPending,
    orderPickup,
    pickupFormSubmitting,
    shippingOption,
    shippingMethod
  } = state

  const partProps = {
    className: st.part,
    ...pick(state, ['country', 'shipBy', 'netToVendor']),
    statusInit: status
  }
  const tableRows = products.map((product) => {
    const {
      comments,
      customization,
      customizationFile,
      variantDescription,
      imageSrc,
      price,
      productName,
      quantity,
      shippingDate,
      type,
      shipAddress,
      shipmentNumber,
      parcels,
      productId,
      imagesReady,
      imagesPacked,
      orderManagementId,
      woodenCrateNeededVendor,
      vendorCode,
      certificationFile,
      woodenCrateValue,
      _id,
      sku,
      purchaseOrderItem,
      material,
      isCategoryLighting,
      slug,
      supplierId,
      isDiscontinued
    } = product
    const countryCode = get(shipAddress, 'country.code', '')
    const isWooden = material === MATERIALS.WOOD
    return {
      customization,
      customizationFile,
      variantDescription,
      formInitialValues: {
        comments,
        productId: _id,
        sku,
        purchaseOrderItem,
        shippingDate: dtFormat(shippingDate, 'YYYY-MM-DD'),
        [`images${ORDER_IMAGE_TYPES.READY}`]: imagesReady,
        [`images${ORDER_IMAGE_TYPES.PACKED}`]: imagesPacked,
        certificationFile,
        parcels
      },
      imageSrc,
      price: price || '',
      productName,
      quantity,
      type,
      shipAddress,
      parcels,
      productId,
      sku,
      shipmentNumber,
      orderManagementId,
      woodenCrate: !!woodenCrateValue && woodenCrateNeededVendor,
      allowCarbCertification:
        Object.keys(CARB_CERTIFICATE_COUNTRIES).includes(countryCode) &&
        isWooden,
      _id,
      vendorCode,
      isCategoryLighting,
      slug,
      supplierId,
      isDiscontinued
    }
  })

  return (
    <InnerPage heading={orderNumber}>
      <TitleIconList className={st.topList} items={menuItems} />
      {state.purchaseOrder ? `PO: ${state.purchaseOrder}` : ''}
      <Part {...partProps}>
        {ordersAreLoading ? (
          <Spinner show width={50} />
        ) : (
          <>
            {tableRows.map((tableRow) => {
              const {
                customization,
                customizationFile,
                variantDescription,
                formInitialValues,
                imageSrc,
                inStock,
                price,
                productName,
                quantity,
                _id,
                sku,
                type,
                shipAddress,
                shipmentNumber,
                orderManagementId,
                isCategoryLighting,
                allowCarbCertification,
                woodenCrate,
                vendorCode,
                slug,
                supplierId,
                isDiscontinued
              } = tableRow

              const supplierIsDifferentThanArtisan =
                supplierId && !sku.startsWith(supplierId)
              const enableLinkToProductPage =
                userType !== USER_TYPES.VENDOR ||
                !supplierIsDifferentThanArtisan

              return (
                <OrderProduct
                  key={_id}
                  id={_id}
                  className={st.orderProduct}
                  customization={customization}
                  customizationFile={customizationFile}
                  variantDescription={variantDescription}
                  productURL={
                    enableLinkToProductPage ? `/product-detail/${sku}` : ''
                  }
                  imagePath={imageSrc}
                  name={productName}
                  type={type}
                  isCategoryLighting={isCategoryLighting}
                  shipAddress={shipAddress}
                  price={price}
                  quantity={quantity}
                  allowCarbCertification={allowCarbCertification}
                  woodenCrate={woodenCrate}
                  vendorCode={vendorCode}
                  slug={slug}
                  isDiscontinued={isDiscontinued}
                >
                  <OrderProductForm
                    id={_id}
                    className={st.form}
                    initialValues={formInitialValues}
                    inputFileCntClassName={st.formInputFile}
                    inStock={inStock}
                    onSubmit={handleSubmit({ shipmentNumber })}
                    isLoading={ordersLoading.includes(_id)}
                    onImageAdd={handleImageAdd}
                    onImageRemove={handleImageRemove}
                    onImageError={handleImageError}
                    imagesLoading={imagesLoading}
                    orderManagementId={orderManagementId}
                    imagesPending={imagesPending}
                    lastSubmittedValues={
                      lastSubmittedValues[_id] || formInitialValues
                    }
                    certificationFile={certificationFiles[_id] || ''}
                    certificationFilePending={certificationFilesPending[_id]}
                    uploadFiles={{
                      handleFileUploadCertification
                    }}
                    deleteFiles={{
                      handleFileDeleteCertification
                    }}
                    canApprove={isAdmin}
                  />
                </OrderProduct>
              )
            })}
            <Row>
              <Col>
                <PickupForm
                  isLoading={pickupFormLoading}
                  onSubmit={handlePickupFormSumbit}
                  pickupFormDataFromContact={{
                    ...pickupFormDataFromContact,
                    companyName: 'Artemest'
                  }}
                  initialValues={{
                    ...pickupFormDataFromContact,
                    ...orderPickup,
                    companyName: 'Artemest'
                  }}
                  intl={intl}
                  pickupFormSubmitting={pickupFormSubmitting}
                />
              </Col>
            </Row>
            {shipmentAllowedUserTypes.includes(userType) && (
              <>
                <Row>
                  <Col>
                    <VendorOrderShipments
                      userType={userType}
                      orderNumber={orderNumber}
                      vendor={vendor}
                      parcels={products.flatMap((product) => product.parcels)}
                      shippingOption={shippingOption}
                      shippingMethod={shippingMethod}
                    />
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </Part>
    </InnerPage>
  )
}

VendorOrderDetail.propTypes = {
  dispatch: func,
  isAdmin: bool,
  intl: shape({
    formatMessage: func
  }),
  match: shape({
    params: shape({
      orderNumber: string,
      vendor: string
    })
  }),
  location: shape({
    search: string
  }),
  userType: string
}

export default connect(({ authReducer }) => ({
  isAdmin: isAdmin(authReducer),
  userType: getUserType(authReducer)
}))(injectIntl(VendorOrderDetail))
